import { useMemo, useState } from "react";
import UiIcon from "../UiIcon";
import RatingScale, { MonthTabs, RatingValue } from "./RatingScale";
import { routineCopy } from "./routineCopy";
import { formatDate, monthQuestions, monthsOf, summarizeMonth } from "./routineModel";
import "../../styles/cvi-routine.css";

// The evaluator's view of one person's Core Responsibilities. Permissions and save rules come from
// the caller: a criterion can be rated only once that month's self-assessment item is submitted,
// and nothing is editable when the period is closed or the viewer is not the evaluator.
export default function ManagerRoutineReview({
    subject, questions = [], answers = [], routineLevels = [], selfReport = null, selfLoading = false,
    readOnly = null, score = null, savingKey = "", error = "", onRate, onComment, onBack = null,
    language = "en", orgLabel = text => text, nameOf = () => null, children = null,
}) {
    const c = routineCopy(language);
    const r = c.review;
    const months = monthsOf(questions);
    const selfAnswers = useMemo(() => (selfReport?.answers || []).filter(a => a.submittedAt), [selfReport]);
    const summaries = useMemo(() => Object.fromEntries(months.map(m => [m, summarizeMonth({
        month: m, questions, selfAnswers, managerAnswers: answers, deadlines: selfReport?.deadlines || [],
        submittedMonths: selfReport?.submittedMonths || [], evaluatedMonths: [], periodOpen: readOnly !== "closed",
    })])), [months.join(","), questions, selfAnswers, answers, selfReport, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps
    const [month, setMonth] = useState(() => months.find(m => summaries[m]?.submitted && summaries[m]?.managerStatus !== "complete") || months[0]);
    const [comments, setComments] = useState({});

    const items = monthQuestions(questions, month);
    const s = summaries[month];
    const answerFor = id => answers.find(a => a.questionId === id) || null;
    const selfFor = id => selfAnswers.find(a => a.questionId === id) || null;
    const answeredInQuarter = questions.filter(q => answerFor(q.id)).length;
    const submittedList = (selfReport?.submittedMonths || []).map(Number).sort().map(c.month).join(", ");

    return <div className="cvi-routine is-review" data-cvi-i18n="managed" lang={language}>
        <header className="cvi-routine-review-head">
            <div>
                {onBack && <button type="button" className="adm-btn adm-btn--sm adm-btn--ghost cvi-routine-back" onClick={onBack}><UiIcon name="chevronLeft" size={14} />{r.back}</button>}
                <span className="cvi-routine-eyebrow">{r.title}</span>
                {onBack && <h2>{subject?.name}</h2>}
                <p>{r.subtitle}</p>
            </div>
            <div className="cvi-routine-score">
                <span>{r.score}</span>
                <strong>{score == null ? "—" : <>{Math.round(score)}<small>/ 100</small></>}</strong>
                <div className="cvi-routine-bar" role="progressbar" aria-valuemin={0} aria-valuemax={questions.length} aria-valuenow={answeredInQuarter} aria-label={r.quarterProgress}>
                    <span style={{ width: `${questions.length ? (answeredInQuarter / questions.length) * 100 : 0}%` }} /></div>
                <small>{r.answered(answeredInQuarter, questions.length)}</small>
            </div>
        </header>

        {error && <div className="adm-callout is-danger" role="alert"><UiIcon name="warning" size={15} /><div>{error}</div></div>}
        {readOnly ? <div className="adm-callout is-neutral" role="note"><UiIcon name="shield" size={15} /><div>{readOnly === "closed" ? r.readOnlyClosed : r.readOnly}</div></div>
            : !selfLoading && selfReport && <p className="cvi-routine-status-line">
                <UiIcon name={submittedList ? "check" : "schedule"} size={14} />{submittedList ? `${r.submittedMonths(submittedList)} ${r.selfRefHelp}.` : r.noneSubmitted}</p>}

        <div className="cvi-routine-toolbar"><MonthTabs months={months} value={month} onChange={setMonth} summaries={summaries} c={c} /></div>

        <section className="cvi-routine-card" aria-labelledby="cvi-routine-review-month">
            <header className="cvi-routine-card-head is-row">
                <h3 id="cvi-routine-review-month">{r.monthCriteria(month)}</h3>
                {items.length > 0 && <span className="cvi-routine-count">{r.monthProgress(items.filter(q => answerFor(q.id)).length, items.length)}</span>}
            </header>
            {items.length === 0 ? <p className="cvi-routine-empty-inline">{c.noQuestions}</p> : <ol className="cvi-routine-criteria">
                {items.map((q, index) => {
                    const ans = answerFor(q.id);
                    const self = selfFor(q.id);
                    const key = `${q.id}:${subject?.id}`;
                    const blocked = !readOnly && !self;
                    const comment = comments[q.id] ?? ans?.note ?? "";
                    const state = ans ? "rated" : readOnly ? "none" : self ? "ready" : "waiting";
                    return <li key={q.id} className={`cvi-routine-criterion is-${state}`}>
                        <div className="cvi-routine-criterion-head">
                            <span className="cvi-routine-eyebrow">{c.criterion(index + 1)}</span>
                            <span className="cvi-routine-criterion-meta">
                                {q.weight != null && <span className="cvi-routine-weight">{c.weight(q.weight)}</span>}
                                <span className={`cvi-routine-chip is-${state}`}><i aria-hidden="true" />{r.criterionState[state]}</span>
                            </span>
                        </div>
                        <h3>{q.label}</h3>
                        <div className="cvi-routine-selfref">
                            <div className="cvi-routine-manager-head">
                                <span className="cvi-routine-label">{r.selfRef}</span>
                                {self ? <RatingValue levels={routineLevels} levelId={self.levelId} tone="self" orgLabel={orgLabel} />
                                    : <span className="cvi-routine-muted">{selfLoading ? "…" : readOnly ? c.notRecorded : r.selfMissing}</span>}
                                {self && <small>{r.selfRefHelp}</small>}
                            </div>
                            {self?.note && <p className="cvi-routine-note">{self.note}</p>}
                            {self?.blockerStatus && self.blockerStatus !== "none" && <p className="cvi-routine-note is-warning">
                                <span>{c.self.challenge} · {c.challenge.statuses[self.blockerStatus] || self.blockerStatus}</span>
                                {[self.blockerText, self.supportNeed && `${c.challenge.support}: ${self.supportNeed}`, self.suggestedAction && `${c.challenge.action}: ${self.suggestedAction}`].filter(Boolean).join(" · ")}</p>}
                        </div>
                        <div className="cvi-routine-row">
                            <span className="cvi-routine-label">{r.yourRating}</span>
                            {readOnly ? <RatingValue levels={routineLevels} levelId={ans?.levelId} orgLabel={orgLabel} emptyText={r.notRated} />
                                : <RatingScale levels={routineLevels} value={ans?.levelId || null} tone="manager" orgLabel={orgLabel}
                                    disabled={blocked || Boolean(savingKey)} busy={savingKey === key} label={`${r.yourRating} · ${q.label}`}
                                    onChange={levelId => onRate?.(q.id, levelId)} />}
                            {savingKey === key && <span className="cvi-routine-save is-saving" aria-live="polite">{r.saving}</span>}
                            {ans && <small className="cvi-routine-muted">{[nameOf(ans.evaluatorId), formatDate(ans.answeredAt, language)].filter(Boolean).join(" · ")}</small>}
                        </div>
                        {readOnly ? ans?.note && <p className="cvi-routine-note"><span>{c.manager.comment}</span>{ans.note}</p>
                            : ans && <label className="cvi-routine-comment"><span>{r.comment}</span>
                                <textarea rows={2} placeholder={r.commentPlaceholder} value={comment} disabled={Boolean(savingKey)}
                                    onChange={e => setComments(prev => ({ ...prev, [q.id]: e.target.value }))}
                                    onBlur={e => { if (e.target.value !== (ans?.note || "")) onComment?.(q.id, ans.levelId, e.target.value); }} /></label>}
                    </li>;
                })}
            </ol>}
            {s && !readOnly && <footer className="cvi-routine-submit is-plain"><p>{c.self.autosave}</p></footer>}
        </section>
        {children}
    </div>;
}

// The evaluator's list of people for one evaluation scope (director and executive workspaces).
export function RoutineMemberList({ title, members = [], questionCount = 0, weightTotal = 0, readOnly = false, error = "", onSelect, language = "en" }) {
    const r = routineCopy(language).review;
    return <div className="cvi-routine is-review" data-cvi-i18n="managed" lang={language}>
        <header className="cvi-routine-review-head"><div><h2>{title}</h2><p>{r.listIntro}</p></div></header>
        {error && <div className="adm-callout is-danger" role="alert"><UiIcon name="warning" size={15} /><div>{error}</div></div>}
        <section className="cvi-routine-card">
            <p className="cvi-routine-muted">{r.weightTotal(questionCount, weightTotal)}{!readOnly && weightTotal !== 100 ? ` · ${r.weightWarning}` : ""}</p>
            {members.length === 0 ? <p className="cvi-routine-empty-inline">{r.noMembers}</p> : <ul className="cvi-routine-people">
                {members.map(m => <li key={m.id}><button type="button" onClick={() => onSelect(m)}>
                    <span className="cvi-routine-avatar" aria-hidden="true">{String(m.name || "?").charAt(0)}</span>
                    <span><strong>{m.name}</strong><small>{r.memberProgress(m.answered, questionCount)}</small></span>
                    <span className="cvi-routine-person-score">{m.score == null ? "—" : Math.round(m.score)}<small>{r.scoreShort}</small></span>
                    <UiIcon name="chevronRight" size={16} />
                </button></li>)}
            </ul>}
        </section>
    </div>;
}
